<script setup>
// Shown when an owner or manager opens their site with ?template=…
// to see it in a template it does not use yet. The server only honours
// that parameter for them, and says so by putting `previewing_from`
// on the theme; this bar is the visible half of the same decision.
//
// While it is up, every internal link keeps the parameter, so the
// whole site can be walked in the previewed design rather than only
// the page the preview was opened on.
//
// The same bar, and the same link keeping, carries ?version=dev: an
// owner or manager looking at the DEVELOPMENT version of the site
// (SiteVersion on the server, which says so with `dev_preview`). Both
// can be up at once — the development version tried in another
// template — so the two parameters travel independently.
import { onBeforeUnmount, onMounted } from 'vue';
import { TEMPLATES } from './templates';
import { siteLocation } from './siteLocation.js';

const props = defineProps({
    theme: { type: Object, default: () => ({}) },
});

function withParams(href, { template, dev }) {
    // Root-relative either way, so the server's link and the browser's
    // are the same string — see siteLocation.js.
    const url = new URL(href, new URL(siteLocation().href, 'http://server.invalid'));
    if (template) url.searchParams.set('template', template);
    else url.searchParams.delete('template');
    if (dev) url.searchParams.set('version', 'dev');
    else url.searchParams.delete('version');
    return url.pathname + url.search + url.hash;
}

const previewing = () => Boolean(props.theme.previewing_from || props.theme.dev_preview);
// What the page is showing now, as parameters: kept on every link.
const current = () => ({
    template: props.theme.previewing_from ? props.theme.template : null,
    dev: Boolean(props.theme.dev_preview),
});

function onClick(e) {
    // Only while a preview is actually up. This component is on every
    // public page, so without this every visitor's menu link was given
    // the site's OWN template as ?template=, on every click.
    if (!previewing()) return;
    const a = e.target.closest && e.target.closest('a');
    if (!a || a.closest('.tiq-tplbar')) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('//') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    // A link written out in full (https://…) is pointing somewhere on
    // purpose, usually the app itself: the Report app, sign-in. On the
    // TrailsIQ address that is the SAME origin as the site, so the
    // check below would pass it and the rewrite would cut it down to
    // a bare path, which the site's own /site/{slug} prefixing then
    // turns into a site page that does not exist. Site pages are
    // written as paths, so only paths are walked in the preview.
    if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return;
    let url;
    try { url = new URL(href, window.location.href); } catch (err) { console.warn('[template preview] unreadable link', href, err); return; }
    if (url.origin !== window.location.origin) return;
    // Donation and form endpoints are not pages.
    if (/\/(donate\/checkout|forms\/)/.test(url.pathname) || url.pathname.endsWith('.ics')) return;
    a.setAttribute('href', withParams(href, current()));
}

onMounted(() => document.addEventListener('click', onClick, true));
onBeforeUnmount(() => document.removeEventListener('click', onClick, true));

function here(key) {
    return withParams(siteLocation().href, { template: key, dev: Boolean(props.theme.dev_preview) });
}
// This page on the live site, as visitors see it.
function live() {
    return withParams(siteLocation().href, { template: null, dev: false });
}
</script>

<template>
    <div v-if="theme.dev_preview" id="tiq-devbar" class="tiq-tplbar tiq-tplbar--dev" role="status">
        <span class="tiq-tplbar__text">
            <strong>Development version</strong><template v-if="theme.previewing_from"> in {{ TEMPLATES[theme.template]?.label }}</template>.
            Visitors still see your live site until you publish it.
        </span>
        <span class="tiq-tplbar__actions">
            <a v-if="theme.previewing_from" :href="here(null)" class="tiq-tplbar__btn">Back to {{ TEMPLATES[theme.previewing_from]?.label || 'its template' }}</a>
            <a href="/settings/website?version=dev" class="tiq-tplbar__btn tiq-devbar__edit">Keep editing</a>
            <a :href="live()" class="tiq-tplbar__btn tiq-tplbar__exit tiq-devbar__live">See the live site</a>
        </span>
    </div>
    <div v-else-if="theme.previewing_from" class="tiq-tplbar" role="status">
        <span class="tiq-tplbar__text">
            Previewing <strong>{{ TEMPLATES[theme.template]?.label }}</strong>.
            Visitors still see {{ TEMPLATES[theme.previewing_from]?.label || 'your current template' }}.
        </span>
        <span class="tiq-tplbar__actions">
            <a v-for="t in Object.values(TEMPLATES).filter((t) => t.key !== theme.template && t.key !== theme.previewing_from)"
               :key="t.key" :href="here(t.key)" class="tiq-tplbar__btn">Try {{ t.label }}</a>
            <a :href="here(null)" class="tiq-tplbar__btn tiq-tplbar__exit">Back to {{ TEMPLATES[theme.previewing_from]?.label || 'my site' }}</a>
        </span>
    </div>
</template>

<style>
.tiq-tplbar {
    position: fixed;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    z-index: 10000;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px 16px;
    max-width: calc(100vw - 32px);
    padding: 10px 14px 10px 18px;
    border-radius: 12px;
    background: #111827;
    color: #fff;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .25);
    font: 500 14px/1.3 system-ui, -apple-system, 'Segoe UI', sans-serif;
}
.tiq-tplbar--dev { background: #1f3b2d; }
.tiq-tplbar__actions { display: flex; flex-wrap: wrap; gap: 8px; }
.tiq-tplbar__btn {
    display: inline-block;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, .35);
    color: #fff;
    text-decoration: none;
    font-weight: 600;
    font-size: 13px;
}
/* Two classes: a site stylesheet that colors its own links white
   otherwise wins on order and leaves the button white on white. */
.tiq-tplbar .tiq-tplbar__exit { background: #fff; color: #111827; border-color: #fff; }
</style>
